import { useState } from 'react';
import { useToast } from '@practium/ui';
import { useCreateTicket, useGetSupportTickets, useReplyToTicket } from '@practium/features/api';
import type { SupportTicketDTO, CreateTicketPayload } from '@practium/features/support';

export const useSupportPage = () => {
	const { success, error } = useToast();

	const [page, setPage] = useState(1);
	const [period, setPeriod] = useState('all');
	const [statusFilter, setStatusFilter] = useState('all');
	const [sortOrder, setSortOrder] = useState('recent');
	const [customDateFrom, setCustomDateFrom] = useState('');
	const [customDateTo, setCustomDateTo] = useState('');
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [replyingTicket, setReplyingTicket] = useState<SupportTicketDTO | null>(null);

	const {
		data: tickets,
		isLoading,
		pagination,
		refetch,
	} = useGetSupportTickets({
		page,
		limit: 6,
		status: statusFilter,
		period,
		sortOrder,
		customDateFrom,
		customDateTo,
	});

	const { mutateAsync: createTicket, isPending: isCreating } = useCreateTicket();
	const { mutateAsync: replyToTicket, isPending: isReplying } = useReplyToTicket();

	const handlePeriodChange = (value: string) => {
		setPeriod(value);
		if (value !== 'custom') {
			setCustomDateFrom('');
			setCustomDateTo('');
		}
		setPage(1);
	};

	const handleStatusChange = (value: string) => {
		setStatusFilter(value);
		setPage(1);
	};

	const handleSortChange = (value: string) => {
		setSortOrder(value);
		setPage(1);
	};

	const handleDateRangeChange = (from: string, to: string) => {
		setCustomDateFrom(from);
		setCustomDateTo(to);
		setPage(1);
	};

	const handleCreate = async (payload: CreateTicketPayload) => {
		try {
			await createTicket(payload);
			setIsCreateModalOpen(false);
			success('Solicitação criada com sucesso!');
		} catch {
			error('Erro ao criar solicitação. Tente novamente.');
		}
	};

	const handleReply = async (message: string) => {
		if (!replyingTicket) return;
		try {
			await replyToTicket({ ticketId: replyingTicket._id, message });
			setReplyingTicket(null);
			await refetch();
			success('Mensagem enviada com sucesso!');
		} catch {
			error('Erro ao enviar mensagem. Tente novamente.');
		}
	};

	return {
		tickets,
		totalDocs: pagination?.totalDocs ?? 0,
		totalPages: pagination?.totalPages ?? 1,
		page,
		setPage,
		isLoading,
		period,
		handlePeriodChange,
		statusFilter,
		handleStatusChange,
		sortOrder,
		handleSortChange,
		customDateFrom,
		customDateTo,
		handleDateRangeChange,
		isCreateModalOpen,
		setIsCreateModalOpen,
		handleCreate,
		replyingTicket,
		setReplyingTicket,
		handleReply,
		isSubmitting: isCreating || isReplying,
	};
};
